import { useEffect, useRef } from "react";
import { siteConfig } from "@/lib/site-config";

interface FormEmbedProps {
  id?: string;
  className?: string;
}

/**
 * Embeds the LeadConnector form widget exactly as provided in the
 * business widget code. No scrolling or compression is imposed, and
 * form_embed.js still owns the final iframe height — the starting height below
 * only reserves the space so first paint does not depend on when that script
 * gets round to measuring.
 */
const FormEmbed = ({ id = `inline-${siteConfig.widgets.formId}`, className = "" }: FormEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    } else {
      // Script already loaded — re-run any global init if available
      // form_embed.js auto-attaches via postMessage, so simply remounting
      // the iframe is enough for it to start measuring.
      try {
        // @ts-expect-error - global form embed init
        if (typeof window.formEmbedInit === "function") window.formEmbedInit();
      } catch {
        // noop
      }
    }
  }, []);

  return (
    <div ref={containerRef} className={`lc-form-slot ${className}`}>
      <iframe
        src={siteConfig.widgets.formIframeSrc}
        // See .lc-form-frame in index.css: the class reserves the height this
        // form actually settles at, so the late resize does not move the hero.
        className="lc-form-frame"
        height={siteConfig.widgets.formHeight}
        style={{ width: "100%", border: "none", borderRadius: 10, display: "block" }}
        id={id}
        data-layout="{'id':'INLINE'}"
        data-trigger-type="alwaysShow"
        data-trigger-value=""
        data-activation-type="alwaysActivated"
        data-activation-value=""
        data-deactivation-type="neverDeactivate"
        data-deactivation-value=""
        data-form-name={siteConfig.widgets.formName}
        data-height={siteConfig.widgets.formHeight}
        data-layout-iframe-id={id}
        data-form-id={siteConfig.widgets.formId}
        loading="lazy"
        title={siteConfig.widgets.formName}
        scrolling="no"
      />
    </div>
  );
};

export default FormEmbed;
